/**
 * SSO bootstrap-token acquisition for the Practice IQ Excel add-in.
 *
 * Primary path: Office.auth.getAccessToken — silently issues a JWT whose
 * audience is api://practiceiq.com.au/{clientId}. The Office host caches the
 * underlying credentials, so calling this is essentially free after the first
 * sign-in.
 *
 * Fallback path: when Office SSO fails (Office host doesn't support it,
 * tenant blocks it, MSA mismatch, etc.) we open an Office Dialog at
 * /dialog.html which runs MSAL.js to do a normal interactive OAuth2 flow,
 * then posts the resulting token back via Office.context.ui.messageParent.
 *
 * In-memory token cache: we keep the most recently acquired token until ~1
 * min before its `exp`. Office host caches too, but caching here saves
 * round-trips to the host on hot paths.
 */

interface CachedToken {
  token: string;
  expEpochMs: number;
}

let cached: CachedToken | null = null;

const FALLBACK_REQUIRED_CODES = new Set<number>([
  13001, // user not signed in to Office
  13002, // user aborted consent
  13003, // unsupported user identity (MSA where AAD expected, etc.)
  13005, // SSO disabled by tenant
  13006, // unspecified runtime error
  13007, // SSO unavailable in this Office host build
  13008, // user triggered another SSO call before this one returned
  13012, // platform doesn't support SSO
]);

function decodeExpiry(token: string): number | null {
  try {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) return null;
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function setCached(token: string) {
  const exp = decodeExpiry(token);
  cached = exp ? { token, expEpochMs: exp } : null;
}

function readCached(): string | null {
  if (!cached) return null;
  if (cached.expEpochMs - 60_000 > Date.now()) return cached.token;
  cached = null;
  return null;
}

export function clearTokenCache() {
  cached = null;
}

/**
 * Returns a valid bootstrap JWT for the API. Uses the Office SSO path when
 * available and falls back to the MSAL dialog otherwise.
 */
export async function getBootstrapToken(): Promise<string> {
  const fromCache = readCached();
  if (fromCache) return fromCache;

  // Outside Office (e.g. browser dev preview), we can't do SSO at all.
  if (typeof Office === 'undefined' || !Office.auth) {
    throw new Error('Office context not available — cannot acquire SSO token');
  }

  if (Office.context.requirements.isSetSupported('IdentityAPI', '1.3')) {
    try {
      const token = await Office.auth.getAccessToken({
        allowSignInPrompt: true,
        allowConsentPrompt: true,
        forMSGraphAccess: false,
      });
      setCached(token);
      return token;
    } catch (err: unknown) {
      const code = (err as { code?: number })?.code;
      if (code !== undefined && !FALLBACK_REQUIRED_CODES.has(code)) {
        // Some Office SSO errors are not recoverable via fallback (e.g.
        // 13000 / 13004 = invalid call). Bubble those.
        throw err;
      }
      // fall through to dialog
    }
  }

  const token = await acquireViaDialog();
  setCached(token);
  return token;
}

interface DialogMessage {
  ok: boolean;
  token?: string;
  error?: string;
}

function acquireViaDialog(): Promise<string> {
  return new Promise((resolve, reject) => {
    const dialogUrl = window.location.origin + '/dialog.html';
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 30, promptBeforeOpen: false },
      (openResult) => {
        if (openResult.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Dialog open failed: ${openResult.error.message}`));
          return;
        }
        const dialog = openResult.value;
        let settled = false;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg) => {
            if (settled) return;
            settled = true;
            dialog.close();
            try {
              const msg = JSON.parse((arg as { message: string }).message) as DialogMessage;
              if (msg.ok && msg.token) {
                resolve(msg.token);
              } else {
                reject(new Error(msg.error ?? 'Sign-in dialog returned no token'));
              }
            } catch (e) {
              reject(new Error(`Malformed dialog message: ${(e as Error).message}`));
            }
          }
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg) => {
            if (settled) return;
            settled = true;
            const code = (arg as { error: number }).error;
            // 12006 = user closed the dialog manually
            reject(new Error(`Sign-in dialog closed (code ${code})`));
          }
        );
      }
    );
  });
}
